import { useState } from "react";
import AccountCombobox from "@app/components/account-combobox";
import type { AccountOption } from "@app/components/account-combobox";

export type FilterDraft = {
  startDate: string | null;
  endDate: string | null;
  accountIds: ReadonlyArray<string>;
};

// Edits a rule's filter subset: start/end date window + the bank-account subset
// (empty = all of the account's accounts). Controlled by the parent.
export default function FilterEditor({
  draft,
  accounts,
  onChange,
}: {
  draft: FilterDraft;
  accounts: ReadonlyArray<AccountOption>;
  onChange: (next: FilterDraft) => void;
}) {
  const [picking, setPicking] = useState(false);
  const chosen = accounts.filter((a) => draft.accountIds.includes(a.id));

  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-500 w-16">From</label>
        <input
          type="date"
          value={draft.startDate ?? ""}
          onChange={(e) =>
            onChange({ ...draft, startDate: e.target.value || null })
          }
          className="rounded border px-2 py-1 text-sm"
        />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-500 w-16">To</label>
        <input
          type="date"
          value={draft.endDate ?? ""}
          onChange={(e) =>
            onChange({ ...draft, endDate: e.target.value || null })
          }
          className="rounded border px-2 py-1 text-sm"
        />
      </div>

      <div>
        <label className="text-xs text-gray-500">Accounts</label>
        <div className="flex flex-wrap items-center gap-1.5 mt-1">
          {chosen.length === 0 && (
            <span className="text-xs text-gray-400">All accounts</span>
          )}
          {chosen.map((a) => (
            <span
              key={a.id}
              className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs"
            >
              {a.nickname || a.name}
              {a.last4 ? ` ••${a.last4}` : ""}
              <button
                type="button"
                onClick={() =>
                  onChange({
                    ...draft,
                    accountIds: draft.accountIds.filter((id) => id !== a.id),
                  })
                }
                className="text-gray-400 hover:text-gray-700"
                aria-label="Remove account"
              >
                ×
              </button>
            </span>
          ))}
          <div className="relative">
            <button
              type="button"
              onClick={() => setPicking((p) => !p)}
              className="text-xs text-emerald-700 hover:underline"
            >
              + add
            </button>
            {picking && (
              <div className="absolute z-20 mt-1 bg-white rounded border shadow-lg p-2">
                <AccountCombobox
                  options={accounts.filter(
                    (a) => !draft.accountIds.includes(a.id)
                  )}
                  allowCreate={false}
                  placeholder="Filter to account…"
                  onPick={(choice) => {
                    setPicking(false);
                    if (choice.bankAccountId)
                      onChange({
                        ...draft,
                        accountIds: [...draft.accountIds, choice.bankAccountId],
                      });
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
